import { bind, Component, effect, h, OmiProps, signal, tag } from "omi";
import classNames from "classnames";
import Dropzone, { DropzoneFile, DropzoneOptions } from "dropzone";

import { NWIcon } from "@/wc/components/atoms/nw-icon";
import { NWLoadingSpinner } from "@/wc/components/base/nw-loadingspinner";
import { formatBytes } from "@/utils/format.ts";
import { networkSignal } from "@/utils/signals";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

type DropzoneProps = {
  /**
   * Called when a file is successfully uploaded
   */
  onUploadSuccess: (file: DropzoneFile | undefined) => void;
  /**
   * Called when a file upload fails
   */
  onUploadError?: (error: string) => void;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Whether a file is currently selected
   */
  hasFile?: boolean;
  /**
   * Existing file that was previously uploaded
   */
  initialFile?: DropzoneFile;
  /**
   * The name of the selected file
   */
  fileName?: string;
  /**
   * Accepted file types
   */
  accept?: string;
  /**
   * Maximum file size in bytes
   */
  maxSize?: number;
  /**
   * Function that returns the upload URL
   */
  getUploadUrl: () => Promise<string | undefined>;
  /**
   * Call to action text
   * @default "Click to upload"
   */
  cta?: string;
  /**
   * Subtext displayed below the CTA
   * @default "PDF, PNG, JPG or GIF (max. 10MB)"
   */
  subtext?: string;
  /**
   * Whether the dropzone is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether to disable drag and drop
   */
  disableDragAndDrop?: boolean;
  /**
   * Called when the dropzone receives focus
   */
  onFocus?: (e: Event) => void;
  /**
   * Called when a file is added
   * @param file
   */
  onAddFile?: (file: DropzoneFile) => void;
  /**
   * Additional class names to apply to the dropzone element
   */
  dropzoneClassNames?: string;
  /**
   * Whether to show a trash icon for errors or the alert indicator
   */
  withTrashIconForErrors?: boolean;
};

type DropzoneOptionsWithDefaultHeader = DropzoneOptions & { defaultHeaders: boolean };

@tag("nw-dropzone")
export class NWDropzone extends Component<DropzoneProps> {
  static css = [tailwind];

  private dropzoneInstance!: Dropzone;
  private uploadProgress = signal<number>(0);
  private uploadStatus = signal<"idle" | "uploading" | "error" | "success">("idle");
  private currentFile = signal<DropzoneFile | undefined>(undefined);
  private isDragActive = signal(false);
  private uploadUrl = networkSignal<string | undefined>();

  installed() {
    const dropzoneElement = this.shadowRoot?.getElementById("dropzone");
    const uploadContainer = this.shadowRoot?.getElementById("upload-container");

    if (!dropzoneElement || !uploadContainer) return;

    if (!this.props.disabled) {
      dropzoneElement.addEventListener("dragenter", this.handleDragEnter);
      dropzoneElement.addEventListener("dragover", this.handleDragOver);
      dropzoneElement.addEventListener("dragleave", this.handleDragLeave);
    }

    if (this.props.initialFile != null) {
      this.currentFile.value = this.props.initialFile;
      this.uploadStatus.value = this.props.initialFile.status === Dropzone.SUCCESS ? "success" : "idle";
    }

    this.dropzoneInstance = new Dropzone(dropzoneElement, {
      url: () => this.uploadUrl.signal.data.value || "",
      uploadMultiple: false,
      acceptedFiles: this.props.accept,
      maxFilesize: this.props.maxSize ?? 10,
      previewsContainer: false,
      autoProcessQueue: true,
      clickable: !this.props.disabled ? [dropzoneElement, uploadContainer!] : false,
      headers: {
        "Content-Type": "application/octet-stream",
      },
      dictFileTooBig: "Upload failed, your file is too big!",
      dictInvalidFileType: "Upload failed, that type of file is not supported",
      dictResponseError: "Upload failed, please try again.",
      method: "put",
      defaultHeaders: false,
      accept: this.handleAccept,
      sending: (file, xhr) => {
        const send = xhr.send;
        xhr.send = function () {
          send.call(xhr, file);
        };
      },
    } as DropzoneOptionsWithDefaultHeader);

    this.dropzoneInstance.on("drop", () => {
      this.isDragActive.value = false;
    });

    if (this.props.onAddFile) {
      this.dropzoneInstance.on("addedfile", file => this.props.onAddFile!(file));
    }

    this.setupDropzoneHandlers();

    effect(() => {
      if (this.props.disableDragAndDrop || !Store.user.isLoggedIn.value) {
        this.dropzoneInstance.disable();
        this.disableDropEvents(dropzoneElement!, uploadContainer!);
      }
    });
  }

  uninstall() {
    const dropzoneElement = this.shadowRoot?.getElementById("dropzone");
    if (dropzoneElement) {
      dropzoneElement.removeEventListener("dragenter", this.handleDragEnter);
      dropzoneElement.removeEventListener("dragover", this.handleDragOver);
      dropzoneElement.removeEventListener("dragleave", this.handleDragLeave);
    }

    if (this.dropzoneInstance) {
      this.dropzoneInstance.off("addedfile");
      this.dropzoneInstance.off("uploadprogress");
      this.dropzoneInstance.off("success");
      this.dropzoneInstance.off("error");
      this.dropzoneInstance.off("drop");
      this.dropzoneInstance.destroy();
    }

    this.uploadProgress.value = 0;
    this.uploadStatus.value = "idle";
    this.currentFile.value = undefined;
    this.isDragActive.value = false;
  }

  receiveProps(props: OmiProps<DropzoneProps>, oldProps: OmiProps<DropzoneProps>) {
    if (props.disabled !== oldProps.disabled) {
      const dropzoneElement = this.shadowRoot?.getElementById("dropzone");
      const uploadContainer = this.shadowRoot?.getElementById("upload-container");

      if (!dropzoneElement || !uploadContainer) return;

      if (this.dropzoneInstance) {
        this.dropzoneInstance.options.clickable = !props.disabled ? [dropzoneElement, uploadContainer!] : false;
      }

      if (props.disabled) {
        this.dropzoneInstance.disable();
        dropzoneElement.removeEventListener("dragenter", this.handleDragEnter);
        dropzoneElement.removeEventListener("dragover", this.handleDragOver);
        dropzoneElement.removeEventListener("dragleave", this.handleDragLeave);
        this.disableDropEvents(dropzoneElement!, uploadContainer!);
      } else {
        this.dropzoneInstance.enable();
        dropzoneElement.addEventListener("dragenter", this.handleDragEnter);
        dropzoneElement.addEventListener("dragover", this.handleDragOver);
        dropzoneElement.addEventListener("dragleave", this.handleDragLeave);
      }

      return true;
    }

    if (props.initialFile?.name !== oldProps.initialFile?.name && props.initialFile) {
      this.currentFile.value = props.initialFile;
      return true;
    }

    return false;
  }

  private handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    if (!this.props.disabled) {
      this.isDragActive.value = true;
    }
  };

  private handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    if (!this.props.disabled) {
      this.isDragActive.value = true;
    }
  };

  private handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    if (!this.props.disabled) {
      const dropzoneElement = this.shadowRoot?.getElementById("dropzone");
      if (!dropzoneElement) return;

      const rect = dropzoneElement.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;

      if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
        this.isDragActive.value = false;
      }
    }
  };

  private handleAccept = async (_file: Dropzone.DropzoneFile, done: (error?: Error) => void) => {
    try {
      await this.uploadUrl.run(async () => await this.props.getUploadUrl());
      done();
    } catch (error) {
      done(error instanceof Error ? error : new Error("Failed to get upload URL"));
    }
  };

  @bind
  private disableDropEvents(...elements: HTMLElement[]) {
    for (const e of elements) {
      e?.addEventListener("drop", e => {
        e.preventDefault();
        this.isDragActive.value = false;
        this.props.onFocus?.(e);
      });
      e?.addEventListener("dragover", e => e.preventDefault());
    }
  }

  @bind
  private setupDropzoneHandlers() {
    this.dropzoneInstance.on("addedfile", (file: DropzoneFile) => {
      this.currentFile.value = file;
      this.uploadStatus.value = "uploading";
    });

    this.dropzoneInstance.on("uploadprogress", (_file: DropzoneFile, progress: number) => {
      this.uploadProgress.value = Math.round(progress);
    });

    this.dropzoneInstance.on("success", () => {
      this.uploadStatus.value = "success";
      this.props.error = undefined;
      if (this.currentFile.value) {
        this.props.onUploadSuccess(this.currentFile.value);
      }
    });

    this.dropzoneInstance.on("error", (_file: DropzoneFile, errorMessage: string) => {
      this.uploadStatus.value = "error";
      if (this.props.onUploadError) {
        const isXMLError = errorMessage.includes("<?xml");
        const message = isXMLError ? "Something went wrong, please refresh the page and try again." : errorMessage;
        this.props.onUploadError(message);
      }
    });
  }

  private handleRetry = () => {
    if (this.currentFile.value) {
      this.dropzoneInstance.removeAllFiles(true);
      this.props.onUploadSuccess(undefined);
      this.uploadStatus.value = "idle";
      this.uploadProgress.value = 0;
      this.props.error = undefined;
      this.currentFile.value = undefined;
    }
  };

  private handleFocus = (e: MouseEvent) => {
    this.props.onFocus?.(e);
  };

  public openFileDialog() {
    if (this.dropzoneInstance && !this.props.disabled) {
      this.dropzoneInstance.element.click();
    }
  }

  private isPdfFile = (file: DropzoneFile): boolean => {
    return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
  };

  render(props: DropzoneProps) {
    const { error, cta = "Click to upload", subtext = "PDF, PNG, JPG or GIF (max. 5MB)", disabled = false } = props;
    const isUploading = this.uploadStatus.value === "uploading";
    const hasError = this.uploadStatus.value === "error" || error || this.uploadUrl.signal.error.value;
    const isSuccess = this.uploadStatus.value === "success";
    const file = this.currentFile.value;
    const hasInitialFile = this.props.initialFile != null;

    return (
      <div className="space-y-4" onClick={this.handleFocus}>
        <div
          id="dropzone"
          className={classNames(
            "border rounded-lg p-6 text-center transition-colors",
            {
              // "border-error-300 bg-error-50": hasError,
              // "border-leather bg-brand-50": isSuccess,
              "border-brand-500 bg-brand-50": this.isDragActive.value && !disabled,
              "border-gray-200 hover:border-brand-500 hover:bg-brand-50": !this.isDragActive.value && !disabled,
              "border-gray-300 cursor-not-allowed": disabled,
              "cursor-pointer": !disabled,
            },
            props.dropzoneClassNames,
          )}
          role="button"
          aria-label="Upload file"
          aria-disabled={disabled}
          tabIndex={disabled ? 0 : 0}
        >
          <div id="upload-container" className="space-y-1">
            <NWIcon
              name="upload-cloud"
              width={20}
              height={20}
              className={classNames("mx-auto shadow-xs border rounded-lg p-2 w-fit h-fit", {
                "text-secondary": !disabled,
                // "text-error-600": hasError,
                "text-disabled": disabled,
              })}
            />

            <div
              className={classNames("mt-2 text-sm select-none", {
                "text-tertiary": !disabled,
                "text-disabled": disabled,
              })}
            >
              <span
                className={classNames("font-semibold", {
                  "text-secondary hover:text-secondary-hover": !disabled,
                  "text-disabled": disabled,
                })}
              >
                {cta}
              </span>{" "}
              or drag and drop
            </div>

            <p className={classNames("mt-1 text-xs", { "text-quaternary": !disabled, "text-disabled": disabled })}>
              {subtext}
            </p>
          </div>
        </div>

        <div id="preview-container" className="space-y-4">
          {file && (
            <div
              className={classNames(
                "border transition-colors min-h-16 flex items-center  relative rounded-xl p-4 bg-white",
                {
                  "border border-error-600": hasError,
                  "border !border-leather": isSuccess,
                  "border border-gray-300  pointer-events-none": disabled,
                  "border-gray-300": !isSuccess && !hasError,
                },
              )}
            >
              {isUploading && (
                <div
                  className="absolute rounded-xl bg-secondary top-0 left-0 bottom-0 transition-all"
                  style={{ width: `${this.uploadProgress.value}%` }}
                ></div>
              )}

              <div className="space-x-3 z-10 flex text-sm flex-1">
                {hasError ? (
                  <NWIcon name="img-file" width={40} height={40} />
                ) : this.isPdfFile(file) ? (
                  <NWIcon
                    name="document-pdf"
                    width={40}
                    height={40}
                    className={classNames("text-tertiary", {
                      "text-disabled": disabled,
                    })}
                  />
                ) : (
                  <img
                    src={file.dataURL}
                    alt=""
                    width={40}
                    height={40}
                    className="rounded object-cover h-10 w-10 z-10"
                  />
                )}

                <div
                  className={classNames("flex justify-between w-full", {
                    "items-center": isUploading,
                  })}
                >
                  <div className="z-10 select-none">
                    <p
                      className={classNames("font-medium truncate max-w-32 sm:max-w-[280px]", {
                        "text-secondary": !disabled,
                        "text-disabled": disabled && !isSuccess,
                        "text-quaternary": disabled && isSuccess,
                      })}
                    >
                      {file.name}
                    </p>
                    <p
                      className={classNames({
                        "text-disabled": disabled,
                        "text-tertiary": !disabled,
                      })}
                    >
                      {isSuccess && file.size != null && `${formatBytes(file.size)} - 100% uploaded`}
                      {hasError && (error || "Upload failed, please try again")}
                      {isUploading && `${this.uploadProgress.value}% uploaded`}
                    </p>
                    {hasError && (
                      <button className="font-semibold mt-1 cursor-pointer" onClick={this.handleRetry}>
                        Try again
                      </button>
                    )}
                  </div>

                  {isUploading && <NWLoadingSpinner size="md" padding="none" />}
                  {hasError && (
                    <NWIcon
                      name={props.withTrashIconForErrors ? "trash" : "alert-circle"}
                      width={20}
                      height={20}
                      onClick={props.withTrashIconForErrors ? this.handleRetry : undefined}
                      className={classNames("text-error-700", {
                        "text-error-800": this.props.disabled,
                        "cursor-pointer": props.withTrashIconForErrors,
                      })}
                    />
                  )}
                  {(isSuccess || (hasInitialFile && !isUploading && !hasError && !isSuccess)) && (
                    <NWIcon
                      name="check-circle"
                      width={20}
                      height={20}
                      className={classNames("text-secondary", {
                        "!text-brand-700/50": this.props.disabled && !isSuccess,
                      })}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
